import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { Filter, WidgetType } from './search_filters';
import MarketplaceFilterRow from './marketplace_filter_row';
import { RCPopover } from '@reverbdotcom/cadence/components';
import SelectFacet from './select_facet';
import FilterChip from './filter_chip';
import { I18N } from './translate';
import { Location } from 'history';
import ListViewToggle from './list_view_toggle';
import TextInputFacet from './text_input_facet';

interface ISearchOverviewListViewToggleProps {
  loading: boolean,
  filters: Filter[],
  setListViewState: (val: boolean) => void
}

interface IProps {
  filters: Filter[];
  followButton?: React.ReactNode;
  tipText?: string;
  totalText?: string;
  titleOverride?: string;
  loading?: boolean;
  resultsTotal?: number;
  trackingQuery?: string;
  location: Location;
  setListViewState?: (val: boolean) => void;
  showListViewToggle?: boolean;
  hideClearFiltersButton?: boolean;
  useH1Title?: boolean;
  pinTextQuery?: boolean; // ensure search bar always visible above grid, instead of buried within filters
  query?: string;
}

// Rewrite of existing Overview component for use in marketplace_filter_row experiment
export default function RQLSearchOverview({
  filters = [],
  followButton = null,
  totalText = '',
  tipText = '',
  loading = false,
  resultsTotal = null,
  trackingQuery = '',
  titleOverride = null,
  location = null,
  setListViewState,
  showListViewToggle = false,
  hideClearFiltersButton = false,
  useH1Title = false,
  pinTextQuery = false,
  query = '',
}: IProps) {
  const sort = filters.find(filter => filter.widgetType === WidgetType.SORT);
  const classes = classNames(
    'search-overview',
    'search-overview--new',
    { 'search-overview--loading': loading },
  );
  const TitleTag = useH1Title ? 'h1' : 'h2';

  const canRenderQueryTitle = !!query && resultsTotal !== null;
  const hasSufficientTitlePropsForSSR = !!titleOverride || canRenderQueryTitle;
  const showLoadingState = loading && filters.length === 0 && !hasSufficientTitlePropsForSSR;
  const textQueryFilter = filters.find((f) => f.key === 'TEXT_QUERY');

  return (
    <nav className={classes}>
      {
        (pinTextQuery && textQueryFilter) && (
          <div className="search-overview__pinned-query-container">
            <div className="search-overview__pinned-query">
              <TextInputFacet
                pageSection="marketplace-pinned-query"
                filter={textQueryFilter}
                trackingQuery={trackingQuery}
                placeholder={I18n.t(`commons.marketplaceFilters.pinnedTextQuery.${location?.pathname?.includes('/shop') ? 'shop' : 'other'}`)}
                hideLabel
              />
            </div>
          </div>
        )
      }
      <section className="search-overview__primary">
        <div className="search-overview__count">
          {showLoadingState &&
            <>
              <div className="search-overview__loading-text" />
              <div className="size-70 mt-1">
                <div className="search-overview__loading-text" />
              </div>
            </>
          }
          {!showLoadingState &&
          <>
            <TitleTag>
              <Title
                filters={filters}
                resultsTotal={resultsTotal}
                totalText={totalText}
                titleOverride={titleOverride}
                query={query}
              />
            </TitleTag>
            <div className="mt-space d-flex fx-align-center">
              {tipText}
              {followButton}
            </div>
          </>
          }
        </div>
        {showListViewToggle &&
          <SearchOverviewListViewToggle
            loading={loading}
            setListViewState={setListViewState}
            filters={filters}
          />
        }
        <div className="search-overview__feed">
          <SortChip
            loading={loading}
            sort={sort}
            trackingQuery={trackingQuery}
          />
        </div>
      </section>
      <div className="search-overview__secondary">
        <MarketplaceFilterRow
          filters={filters}
          loading={loading}
          trackingQuery={trackingQuery}
          displayCount
          location={location}
          totalText={totalText}
          hideOnMobile
          dynamicTitle
          setListViewState={setListViewState}
          showListViewToggle={showListViewToggle}
          hideClearFiltersButton={hideClearFiltersButton}
          pinTextQuery={pinTextQuery}
        />
      </div>
    </nav>
  );
}

export function Title({ filters, resultsTotal, totalText, titleOverride, query }) {
  if (titleOverride) {
    return titleOverride;
  }

  const queryFilter = filters.filter(f => {
    return f.key === 'TEXT_QUERY';
  });

  const queryStringFromFilter = queryFilter && queryFilter.length > 0
    ? queryFilter[0].options[0].optionValue
    : null;

  /* On server side renders we will default to using the query string while client side renders
  will pull from the filter query to ensure the title is properly updated */
  const queryString = queryStringFromFilter || query;

  if (queryString && resultsTotal) {
    return (
      <I18N
        text="commons.marketplaceFilters.resultsCountFor"
        args={{ count: I18n.toNumber(resultsTotal, { precision: 0 }) }}
      >
        <span className="weight-bold">
          &ldquo;{queryString}&rdquo;
        </span>
      </I18N>
    );
  }

  if (resultsTotal) {
    return (
      <I18N
        text="commons.marketplaceFilters.resultsCount"
        args={{ count: I18n.toNumber(resultsTotal, { precision: 0 }) }}
      />
    );
  }

  return totalText;
}

export function SortChip({ loading, sort, trackingQuery }) {
  const [isPopoverOpen, openPopover] = React.useState(false);

  if (loading && !sort) {
    return (
      <div className="mobile-d-none">
        <FilterChip
          key="sort"
          loading
        >
          <div className="filter-chip__loading-placeholder" />
        </FilterChip>
      </div>
    );
  }
  if (!sort) return null;

  const selected = sort?.options.find(opt => opt.selected);
  return (
    <div className="mobile-d-none">
      <RCPopover
        isOpen={isPopoverOpen}
        onDismiss={() => openPopover(false)}
        loading={loading}
        anchor={
          <FilterChip
            withDropdown
            loading={loading}
            onClick={() => openPopover(!isPopoverOpen)}
          >
            <span>
              <span className="weight-bold">{`${sort.name}: `}</span>
              {selected?.name || ''}
            </span>
          </FilterChip>
        }
      >
        <SelectFacet
          pageSection="search-overview"
          filter={sort}
          trackingQuery={trackingQuery}
        />
      </RCPopover>
    </div>
  );
}

function SearchOverviewListViewToggle({ loading, filters, setListViewState }: ISearchOverviewListViewToggleProps) {
  return (
    <div className="search-overview__list-view-toggle">
      {(loading && filters.length === 0) ? (
        <div className="search-overview__list-view-toggle-loading-placeholder" />
      ) :
        (
          <ListViewToggle
            setListViewState={setListViewState}
            loading={loading}
            id="search-overview"
          />
        )}
    </div>
  );
}
